import { computed, inject, Injectable, signal } from "@angular/core";
import { LoggedUserService } from "../logged-user/logged-user.service";
import { StorageService } from "../storage/storage.service";
import { NotificationService } from "../notification/notification.service";
import { NgxPermissionsService } from "ngx-permissions";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { lastValueFrom, map, Observable } from "rxjs";
import { Router } from "@angular/router";
import { NonPageResult } from "../../store/entities/base/base.model";
import { MatDialog } from "@angular/material/dialog";
import { UserStore } from "../../store/entities/user-management/user/user.store";
import { GenericStore } from "../../store/entities/base/generic.store";
import {
  AuthTokenModel,
  User,
} from "../../store/entities/user-management/user/user.model";
import { ENV } from "../../../env/env.dev";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private userStore = inject(UserStore);
  private permissions = inject(NgxPermissionsService);
  private notify = inject(NotificationService);
  private storage = inject(StorageService);
  private loggedUser = inject(LoggedUserService);
  private dialogRef = inject(MatDialog);

  // Private signals
  private _isAuthenticating = signal<boolean>(false);
  private _authError = signal<string | null>(null);
  private _accessToken = signal<string | null>(null);
  private _isKillingSession = false;
  private _lastErrorKey = "";
  private _lastErrorTime = 0;
  private readonly ERROR_THROTTLE_MS = 3000; // Don't show same error within 3 seconds

  // Public readonly signals
  readonly isAuthenticating = this._isAuthenticating.asReadonly();
  readonly authError = this._authError.asReadonly();

  // Computed signal for login status - now based on a signal, not localStorage directly
  readonly isLoggedIn = computed(() => !!this._accessToken());

  constructor() {
    // Initialize the token signal from localStorage
    const token = this.storage?.get("accessToken");
    if (token) {
      this._accessToken.set(token);
    }
  }

  refreshToken(): Observable<AuthTokenModel> {
    const refreshToken = this.storage.get("refreshToken");
    const accessToken = this.storage.get("accessToken");

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    const body = JSON.stringify({
      refreshToken: refreshToken,
    });

    return this.http
      .post<AuthTokenModel | any>(ENV.serverUrl + "/auth/refresh-token", body, {
        headers,
      })
      .pipe(
        map((tokenDetails) => {
          if (tokenDetails?.access_token) {
            this.storage.set("accessToken", tokenDetails.access_token);
            this.storage.set("refreshToken", tokenDetails.refresh_token);
            this.storage.set("expiresIn", tokenDetails.expires_in);

            // Update the signal to trigger isLoggedIn computed
            this._accessToken.set(tokenDetails.access_token);
          }
          return tokenDetails;
        }),
      );
  }

  async authRole() {
    const accessToken = this.storage.get("accessToken");

    if (!accessToken) {
      // No token, user is not logged in - this is normal, don't throw error
      return null;
    }

    try {
      const headers = new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "Access-Control-Allow-Origin": "*",
      });
      return await lastValueFrom(
        this.http.get<any>(ENV.serverUrl + "/auth/user-info", { headers }).pipe(
          map((response) => {
            // Extract authorities array from response
            const objectPerms: { authority: string }[] =
              response?.authorities || [];
            const finalArray: string[] = objectPerms.map(
              (obj) => obj.authority,
            );

            // Add a trusted client permission for every successful login
            finalArray.push("ROLE_TRUSTED_CLIENT");
            this.permissions.loadPermissions(finalArray);

            // Extract user details from response
            // Adjust these fields based on your actual backend response structure
            const userDetail = response;
            // console.log("User Details:", userDetail);
            //update logged user details
            this.loggedUser.setLoggedUser({
              id: userDetail?.attributes?.id,
              uid: userDetail?.attributes?.uid,
              email: userDetail?.attributes?.email,
              fullName: userDetail?.name,
            });

            this.userStore.getMyDetails();

            return response;
          }),
        ),
      );
    } catch (error) {
      this.killSession();
      throw error;
    }
  }

  async login(formData: any): Promise<AuthTokenModel> {
    this._isAuthenticating.set(true);
    this._authError.set(null);
    const body = JSON.stringify({
      username: formData.email,
      password: formData.password,
    });

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    try {
      const result = await lastValueFrom(
        this.http
          .post<AuthTokenModel | any>(ENV.serverUrl + "/auth/login", body, {
            headers,
          })
          .pipe(
            map((data) => {
              if (data && data.access_token) {
                this.storage.set("accessToken", data.access_token);
                this.storage.set("refreshToken", data.refresh_token);
                this.storage.set("expiresIn", data.expires_in);

                // Update the signal to trigger isLoggedIn computed
                this._accessToken.set(data.access_token);
              } else {
                this._authError.set(`${data?.code}: ${data?.message}`);
                this.notify.error(`${data?.code}: ${data?.message}`);
                throw new Error(`Login failed: ${data?.message}`);
              }
              return data;
            }),
          ),
      );
      return result;
    } catch (error) {
      this._authError.set("Login failed");
      throw error;
    } finally {
      this._isAuthenticating.set(false);
    }
  }

  async forgetPassword(formData: { email: string }): Promise<string> {
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    const body = JSON.stringify({
      email: formData.email,
    });

    // responseType 'text' avoids Angular's JSON parsing, since this endpoint
    // can reply 200 with a plain-text or empty body (which 'json' would
    // otherwise fail to parse and surface as a false HttpErrorResponse).
    return await lastValueFrom(
      this.http.post(ENV.serverUrl + "/auth/forgot-password", body, {
        headers,
        responseType: "text",
      }),
    );
  }

  /** Plain REST: the endpoint answers with text. */
  async activateAccount(formData: {
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<string> {
    return await lastValueFrom(
      this.http.post(ENV.serverUrl + `/auth/reset-password`, formData, {
        responseType: "text",
      }),
    );
  }

  async resetPassword(formData: {
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<string> {
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    const body = JSON.stringify({
      token: formData?.token,
      newPassword: formData?.newPassword,
      confirmPassword: formData?.confirmPassword,
    });

    return await lastValueFrom(
      this.http.post(ENV.serverUrl + `/auth/reset-password`, body, {
        headers,
        responseType: "text",
      }),
    );
  }

  async logout() {
    const accessToken = this.storage.get("accessToken");
    const refreshToken = this.storage.get("refreshToken");

    // Always complete logout, even if server request fails
    try {
      const body = JSON.stringify({
        refreshToken: refreshToken,
        accessToken: accessToken,
      });

      const headers = new HttpHeaders({
        "Content-Type": "application/json",
      });

      await lastValueFrom(
        this.http
          .post<AuthTokenModel | any>(ENV.serverUrl + "/auth/logout", body, {
            headers,
          })
          .pipe(
            map((data) => {
              if (data?.data) {
                this.notify.info(data?.data);
              }
              return data;
            }),
          ),
      );
    } catch (error: any) {
      // Log error but don't prevent logout
    } finally {
      // Always kill session regardless of server response
      this.killSession({ loggedOut: true });
    }
  }

  private killSession(data?: { loggedOut?: boolean; expired?: boolean }) {
    // Prevent multiple simultaneous killSession calls
    if (this._isKillingSession) {
      return;
    }

    this._isKillingSession = true;

    try {
      // Clear tokens FIRST before navigation
      this.storage.remove("accessToken");
      this.storage.remove("refreshToken");
      this.storage.remove("expiresIn");

      // Update the signal to trigger isLoggedIn computed
      this._accessToken.set(null);

      if (data?.loggedOut) {
        this.storage.set("loggedOut", true);
      }
      if (data?.expired) {
        this.storage.set("expired", true);
      }

      this.dialogRef.closeAll();
      this.loggedUser.clearLoggedUser();

      // Clear permissions
      this.permissions.flushPermissions();

      // Use navigateByUrl for cleaner navigation
      this.router.navigateByUrl("/login", { replaceUrl: true });
    } catch (error) {
      // Force navigation even if there's an error
      this.router.navigateByUrl("/login", { replaceUrl: true });
    } finally {
      // every store, including the signed-in user, so the next login starts clean
      GenericStore.resetAll();
      // Reset the flag after a short delay to allow navigation to complete
      setTimeout(() => {
        this._isKillingSession = false;
      }, 1000);
    }
  }

  public collectFailedRequest(err: {
    reason: string;
    status: number;
    causedBy: string;
  }): void {
    const errorCode = err.status;
    const now = Date.now();

    if (errorCode === 200) {
      //@todo -- special actions for success
    } else if (errorCode === 400) {
      const message = "Authentication Failed!";
      this._showThrottledError(message + " : " + errorCode?.toString(), now);
      // this.killSession();
    } else if (errorCode === 401) {
      // Only kill session if not already logged out
      if (this._accessToken()) {
        const message = "Unauthorized Access";
        this._showThrottledError(message + " : " + errorCode?.toString(), now);
        this.killSession();
      }
    } else if (errorCode === 404) {
      const message = "Resource not Found";
      this._showThrottledError(message + " : " + errorCode?.toString(), now);
    } else if (errorCode === 500) {
      const message = "Internal Server Error";
      this._showThrottledError(message + " : " + errorCode?.toString(), now);
    } else if (errorCode === 504) {
      const message = "Service Temporary Unavailable";
      this._showThrottledError(message + " : " + errorCode?.toString(), now);
    } else {
      const message = "Failed to perform operation";
      this._showThrottledError(message + " : " + errorCode?.toString(), now);
    }
  }

  private _showThrottledError(message: string, now: number): void {
    // Prevent showing the same error within throttle period
    if (
      message === this._lastErrorKey &&
      now - this._lastErrorTime < this.ERROR_THROTTLE_MS
    ) {
      return;
    }

    this._lastErrorKey = message;
    this._lastErrorTime = now;
    this.notify.error(message);
  }
}
